import React from 'react';

import moment from 'moment';

import type { ContactActivity } from '../../typescript-types/contact/contact-activity';

import {
  statusDisplay,
  SAVED_STATUS,
  VALIDATING_STATUS,
} from '../../config/application-status-config';
import {
  ACTIVITY_TYPES,
  ACTIVITY_TYPE_VALUES,
  ContactActivityValueType,
} from '../../util/activity-types';
import { capitalize, formatDuration } from '../../util/formatters';
import { ICON_NAMES, MaterialDesignIcon } from '../../util/material-design-icons';
import { MESSAGE_TYPE } from '../../util/message-types';
import { WebhookEventLogLabel } from '../../webhook-event-subscription/utils/webhook-event-log-utils';

type StatusChangeActivityDetailProps = {
  contactActivity: ContactActivity;
  applicant: FullApplicant | JobApplicant;
};

const StatusChangeAdditionalDetails = ({
  contactActivity,
  applicant,
}: StatusChangeActivityDetailProps) => {
  const { status } = contactActivity;

  const reachOutAlertMoment = applicant.reachOutAlertDate
    ? moment(applicant.reachOutAlertDate)
    : null;

  return (
    <>
      {status?.statusChangeReason ? (
        <div className="typo-subtitle">
          {status.statusChangeReason.reasonDescription}
          {status.reasonExplanation && ` - ${status.reasonExplanation}`}
          {status.status === SAVED_STATUS && reachOutAlertMoment ? (
            <div className="u-display-flex">
              <MaterialDesignIcon
                iconName={ICON_NAMES.EVENT}
                color="gray"
                className="u-margin-right-sm u-margin-top-xs"
                size="xs"
              />
              <div>
                <div>Saved Applicant Reminder</div>
                <div>
                  {moment().isBefore(reachOutAlertMoment, 'minute') ? 'Scheduled for' : 'Sent on'}{' '}
                  {reachOutAlertMoment.format('ddd, MMM Do')}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
      {contactActivity.WebhookEventLogs?.length ? (
        <>
          {contactActivity.WebhookEventLogs.map((log) =>
            log ? (
              <div key={`webhookEventLog-${log.webhookEventLogId}`}>
                <WebhookEventLogLabel eventLog={log} />
              </div>
            ) : null,
          )}
        </>
      ) : null}
      {status?.status === VALIDATING_STATUS && contactActivity.isAutomated ? (
        <div className="typo-subtitle">
          We have combined Finalizing Hire and Awaiting Start into one Onboarding status to
          centralize your pre-hire needs in one place! Read more about this{' '}
          <a
            className="typo-link"
            rel="noopener noreferrer"
            target="_blank"
            href="https://help.teamengine.io/article/35-the-hiring-process-and-applicant-tracking-system"
          >
            here
          </a>
          .
        </div>
      ) : null}
    </>
  );
};

type AdditionDetailExtraData = { fullApplicant?: FullApplicant; jobApplicant?: JobApplicant };

type ContactActivityItemConfiguration = {
  [key in ContactActivityValueType]:
    | {
        getActivityDescription: (contactActivity: ContactActivity) => string | undefined;
        getActivityLabel: (contactActivity?: ContactActivity) => string | undefined;
        getIconComponentName: (contactActivity: ContactActivity) => string;
        getAdditionalDetails?: (
          contactActivity: ContactActivity,
          extraData: AdditionDetailExtraData,
        ) => JSX.Element | undefined;
      }
    | undefined;
};

export const isValidActivityType = (value: string): value is ContactActivityValueType =>
  Boolean(ACTIVITY_TYPE_VALUES[value]);

/**
 * This object has all of the things we need to display in the activity feeds.
 *
 * Whenever we add a new contact activity, we need to update this object to show it in the activity feeds.
 *
 * There are two layers of the use of "undefined" below:
 *  - It's used to denote an contact activity type that we explicitly don't want to show in the feed by putting
 *     it on the top level
 *  - It's used by both of `getActivityDescription` and `getIconComponentName` to say "this contact activity doesn't
 *     have all the information I need to know what to return". Note: this is rare and should be handled
 *     differently in general, but when you have nested data structures like we rely on with contact activity,
 *     you wind up having to handle this situation.
 *
 * This is currently only in use for applicants, but there's no reason that we couldn't show an activity
 *      feed for other entity types (in particular, the employee type). This file wouldn't change all that
 *      much honestly, but we'd need to actually implement the quit/fire items, remove references to
 *      "applicant", and pass employee information to the additional details function.
 *
 * Please keep the keys in alphabetical order! Bonus points for consistent method ordering too.
 */
export const CONTACT_ACTIVITY_ITEM_CONFIGURATION: ContactActivityItemConfiguration = {
  [ACTIVITY_TYPES.ADD_FILE]: {
    getActivityDescription: () => 'uploaded a document',
    getActivityLabel: () => 'Uploaded a document',
    getIconComponentName: () => ICON_NAMES.FILE_COPY,
  },
  [ACTIVITY_TYPES.CALL]: {
    getActivityDescription: (contactActivity: ContactActivity) => {
      const { call } = contactActivity;
      if (!call) {
        return undefined;
      }
      return call.isInbound ? 'called' : 'called the applicant';
    },
    getActivityLabel: () => 'Phone Call',
    getIconComponentName: () => ICON_NAMES.CALL,
    getAdditionalDetails: (contactActivity: ContactActivity) => {
      const { call } = contactActivity;
      const duration = call?.durationSeconds;
      if (duration === undefined || duration === null) {
        return undefined;
      }
      return <div className="typo-subtitle">{formatDuration(duration)}</div>;
    },
  },
  [ACTIVITY_TYPES.COMMENT]: {
    getActivityDescription: () => 'added a note',
    getActivityLabel: () => 'Notes',
    getIconComponentName: () => ICON_NAMES.COMMENT,
    getAdditionalDetails: (contactActivity: ContactActivity) => (
      <div
        dangerouslySetInnerHTML={{
          __html: contactActivity.comment?.comment || '',
        }}
      />
    ),
  },
  // deprecated
  [ACTIVITY_TYPES.CONTACT]: undefined,
  // Not implemented
  [ACTIVITY_TYPES.EMP_MANUALLY_ADDED]: undefined,
  // Not implemented
  [ACTIVITY_TYPES.EMP_QUIT_FIRED]: undefined,
  [ACTIVITY_TYPES.FINALIZING_HIRE_STEP]: {
    getActivityDescription: (contactActivity: ContactActivity) => {
      const { checklist } = contactActivity;
      if (!checklist) {
        return undefined;
      }
      return `marked ${checklist.step.description} as ${checklist.status} for applicant in checklist`;
    },
    getActivityLabel: () => 'Pre-hire checklist item change',
    getIconComponentName: () => ICON_NAMES.CHECK,
  },
  [ACTIVITY_TYPES.MESSAGE]: {
    getActivityDescription: (contactActivity: ContactActivity) => {
      if (!contactActivity.message) {
        return undefined;
      }
      return `sent ${
        contactActivity.message.type === MESSAGE_TYPE.EMAIL ? 'an email' : 'a text message'
      }`;
    },
    getActivityLabel: (contactActivity?: ContactActivity) => {
      let label = 'Message';
      if (!contactActivity) {
        return label;
      }
      const { message } = contactActivity;
      if (!message) {
        return label;
      }
      if (message.isInbound) {
        label = `${capitalize(message.type)} ${label}`;
      }
      if (message.isInbound) {
        label = `Incoming ${label}`;
      }

      return label;
    },
    getIconComponentName: (contactActivity: ContactActivity) => {
      if (contactActivity.message?.type === MESSAGE_TYPE.EMAIL) {
        return ICON_NAMES.EMAIL;
      }
      return ICON_NAMES.SMS;
    },
    getAdditionalDetails: (contactActivity: ContactActivity) => (
      <div
        dangerouslySetInnerHTML={{
          __html: contactActivity.message?.messageBody || '',
        }}
      />
    ),
  },
  [ACTIVITY_TYPES.REMOVE_FILE]: {
    getActivityDescription: () => 'removed a document',
    getActivityLabel: () => 'Removed a document',
    getIconComponentName: () => ICON_NAMES.DELETE_FOREVER,
  },
  [ACTIVITY_TYPES.SHARE]: {
    getActivityDescription: () => 'shared the applicant',
    getActivityLabel: () => 'Shared',
    getIconComponentName: () => ICON_NAMES.CONTACT_MAIL,
    getAdditionalDetails: (contactActivity: ContactActivity) => {
      const { ApplicantShareLog } = contactActivity;
      if (!ApplicantShareLog) {
        return undefined;
      }
      return <div className="typo-subtitle">To: {ApplicantShareLog.toEmails}</div>;
    },
  },
  [ACTIVITY_TYPES.STATUS]: {
    getActivityDescription: (contactActivity: ContactActivity) => {
      const { status } = contactActivity;
      // If this is true, then it was activity by the applicant
      if (!contactActivity.isAutomated && contactActivity.user === null) {
        return 'Applied';
      }
      if (!status) {
        return undefined;
      }
      return `changed applicant status to ${statusDisplay(status.status)}`;
    },
    getActivityLabel: () => 'Status Change',
    getIconComponentName: (contactActivity: ContactActivity) => {
      if (contactActivity.user === null) {
        return ICON_NAMES.PERSON;
      }
      return ICON_NAMES.SWAP_HORIZ;
    },
    getAdditionalDetails: (
      contactActivity: ContactActivity,
      { fullApplicant, jobApplicant }: AdditionDetailExtraData,
    ) => {
      const applicant = fullApplicant || jobApplicant;
      if (!applicant) {
        return undefined;
      }
      return (
        <StatusChangeAdditionalDetails contactActivity={contactActivity} applicant={applicant} />
      );
    },
  },
  [ACTIVITY_TYPES.UPDATE]: {
    getActivityDescription: () => 'updated their application',
    getActivityLabel: () => 'Application Updated',
    getIconComponentName: () => ICON_NAMES.PERSON_ADD,
  },
} as const;

export const CONTACT_ACTIVITY_ITEM_LABELS = Object.keys(CONTACT_ACTIVITY_ITEM_CONFIGURATION).reduce(
  (acc, curr) => {
    if (!isValidActivityType(curr)) {
      return acc;
    }
    const config = CONTACT_ACTIVITY_ITEM_CONFIGURATION[curr];
    if (!config) {
      return acc;
    }

    acc[curr] = config.getActivityLabel() || '';
    return acc;
  },
  {} as { [key in keyof typeof CONTACT_ACTIVITY_ITEM_CONFIGURATION]: string },
);
